import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { LayoutDashboard, Package, PanelsTopLeft, Bluetooth, Truck } from "@/components/icons";
import {
  Button,
  Skeleton,
  ToggleGroup,
  ToggleGroupItem,
  toast,
} from "@/components/kit";
import { cn } from "@/lib/utils";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import {
  useMarkOrderDelivered,
  useMarkOrderPickedUp,
  useMarkOrderReady,
  useMarkOrderShipped,
} from "@/modules/orders/presentation/api/queries";
import { useNavigate, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import type { BatchPrintOrderRef } from "@/modules/chat-tools/presentation/lib/load-batch-label-items";
import { BatchBluetoothPrintDialog } from "@/modules/chat-tools/presentation/ui/BatchBluetoothPrintDialog";
import { isWebBluetoothSupported } from "@/modules/chat-tools/presentation/lib/web-bluetooth";
import { useFulfillmentBoardQuery } from "../api/queries";
import { FulfillmentTable } from "../ui/FulfillmentTable";
import { KanbanColumn } from "../ui/KanbanColumn";

type ViewMode = "kanban" | "table";
type Scope = "today" | "all";
type MobileColumn = "paid" | "ready_to_ship" | "shipped";

const MOBILE_TABS: Array<{ value: MobileColumn; label: string }> = [
  { value: "paid", label: "ລໍຖ້າແພັກ" },
  { value: "ready_to_ship", label: "ພ້ອມສົ່ງ" },
  { value: "shipped", label: "ສົ່ງແລ້ວ" },
];

export function FulfillmentBoardPage() {
  const nav = useNavigate();
  const canManage = useActionPermission(["orders:write"]);
  const bluetoothSupported = isWebBluetoothSupported();
  const [scope, setScope] = useState<Scope>("all");
  const [view, setView] = useState<ViewMode>("kanban");
  const [mobileTab, setMobileTab] = useState<MobileColumn>("paid");
  const [batchPrintOpen, setBatchPrintOpen] = useState(false);
  const [batchPrintOrders, setBatchPrintOrders] = useState<BatchPrintOrderRef[]>(
    [],
  );
  const [pendingReadyId, setPendingReadyId] = useState<string | null>(null);
  const [pendingShippedId, setPendingShippedId] = useState<string | null>(null);
  const [pendingDeliveredId, setPendingDeliveredId] = useState<string | null>(null);
  const [pendingPickedUpId, setPendingPickedUpId] = useState<string | null>(null);

  const board = useFulfillmentBoardQuery({ scope });
  const markReady = useMarkOrderReady();
  const markShipped = useMarkOrderShipped();
  const markDelivered = useMarkOrderDelivered();
  const markPickedUp = useMarkOrderPickedUp();

  const columns = board.data?.columns;
  const counts = board.data?.meta?.counts;

  const flatOrders = useMemo(() => {
    if (!columns) return [];
    return [
      ...columns.paid,
      ...columns.ready_to_ship,
      ...columns.shipped,
    ];
  }, [columns]);

  const labelPrintOrders = useMemo(() => {
    if (!columns) return [];
    return [...columns.paid, ...columns.ready_to_ship].map((order) => ({
      id: order.id,
      orderNumber: order.orderNumber,
      customerName: order.customerName,
    }));
  }, [columns]);

  function openBatchPrint(orders: BatchPrintOrderRef[]) {
    setBatchPrintOrders(orders);
    setBatchPrintOpen(true);
  }

  function goToOrder(id: string) {
    nav({ to: "/app/orders/$id", params: { id } });
  }

  function handleMarkReady(id: string) {
    setPendingReadyId(id);
    markReady.mutate(
      { id },
      {
        onSuccess: () => toast.success("ແພັກເສັດແລ້ວ"),
        onError: (e) =>
          toast.error("ອັບເດດບໍ່ສຳເລັດ", {
            description: e instanceof Error ? e.message : undefined,
          }),
        onSettled: () => setPendingReadyId(null),
      },
    );
  }

  function handleMarkShipped(id: string, tracking: string | null) {
    setPendingShippedId(id);
    markShipped.mutate(
      { id, input: { trackingNumber: tracking } },
      {
        onSuccess: () => toast.success("ບັນທຶກສົ່ງແລ້ວ"),
        onError: (e) =>
          toast.error("ອັບເດດບໍ່ສຳເລັດ", {
            description: e instanceof Error ? e.message : undefined,
          }),
        onSettled: () => setPendingShippedId(null),
      },
    );
  }

  function handleMarkDelivered(id: string) {
    setPendingDeliveredId(id);
    markDelivered.mutate(
      { id },
      {
        onSuccess: () => toast.success("ບັນທຶກຂົນສົ່ງສົ່ງຮອດແລ້ວ"),
        onError: (e) =>
          toast.error("ອັບເດດບໍ່ສຳເລັດ", {
            description: e instanceof Error ? e.message : undefined,
          }),
        onSettled: () => setPendingDeliveredId(null),
      },
    );
  }

  function handleMarkPickedUp(id: string) {
    setPendingPickedUpId(id);
    markPickedUp.mutate(
      { id },
      {
        onSuccess: () => toast.success("ປິດອໍເດີ້ແລ້ວ"),
        onError: (e) =>
          toast.error("ອັບເດດບໍ່ສຳເລັດ", {
            description: e instanceof Error ? e.message : undefined,
          }),
        onSettled: () => setPendingPickedUpId(null),
      },
    );
  }

  const columnProps = {
    isLoading: board.isLoading,
    canManage: !!canManage,
    onView: goToOrder,
    onMarkReady: canManage ? handleMarkReady : undefined,
    onMarkShipped: canManage ? handleMarkShipped : undefined,
    onMarkDelivered: canManage ? handleMarkDelivered : undefined,
    onMarkPickedUp: canManage ? handleMarkPickedUp : undefined,
    markReadyId: pendingReadyId,
    markShippedId: pendingShippedId,
    markDeliveredId: pendingDeliveredId,
    markPickedUpId: pendingPickedUpId,
  };

  return (
    <>
      <Header />
      <Main className="flex flex-col gap-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex flex-col gap-1">
            <h2 className="font-display text-2xl text-foreground sm:text-3xl">
              ຈັດສົ່ງ
            </h2>
            <p className="text-muted-foreground text-sm">
              ແພັກ ແລະ ສົ່ງອໍເດີ້ — {counts?.total ?? 0} ລາຍການ
            </p>
            <div className="mt-2 h-0.5 w-16 rounded-full bg-secondary/70" />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button asChild className="rounded-full">
              <Link to="/app/fulfillment/pack">
                <Package />
                ໂໝດແພັກ
              </Link>
            </Button>
            {bluetoothSupported && labelPrintOrders.length > 0 ? (
              <Button
                variant="outline"
                className="rounded-full"
                onClick={() => openBatchPrint(labelPrintOrders)}
              >
                <Bluetooth data-icon="inline-start" />
                ພິມປ້າຍຊຸດ ({labelPrintOrders.length})
              </Button>
            ) : null}
            <ToggleGroup
              type="single"
              value={scope}
              onValueChange={(v) => v && setScope(v as Scope)}
              variant="outline"
              size="sm"
            >
              <ToggleGroupItem value="today" className="rounded-full px-4">
                ມື້ນີ້
              </ToggleGroupItem>
              <ToggleGroupItem value="all" className="rounded-full px-4">
                ທັງໝົດ
              </ToggleGroupItem>
            </ToggleGroup>
            <ToggleGroup
              type="single"
              value={view}
              onValueChange={(v) => v && setView(v as ViewMode)}
              variant="outline"
              size="sm"
            >
              <ToggleGroupItem value="kanban" className="rounded-full px-3">
                <LayoutDashboard className="size-4" />
              </ToggleGroupItem>
              <ToggleGroupItem value="table" className="rounded-full px-3">
                <PanelsTopLeft className="size-4" />
              </ToggleGroupItem>
            </ToggleGroup>
          </div>
        </div>

        {board.isLoading ? (
          <div className="grid gap-4 lg:grid-cols-3">
            {["paid", "ready", "shipped"].map((id) => (
              <Skeleton key={id} className="h-80 rounded-2xl" />
            ))}
          </div>
        ) : view === "table" ? (
          <FulfillmentTable
            orders={flatOrders}
            onRowClick={goToOrder}
          />
        ) : (
          <>
            <div className="flex gap-2 lg:hidden">
              {MOBILE_TABS.map((tab) => (
                <button
                  key={tab.value}
                  type="button"
                  onClick={() => setMobileTab(tab.value)}
                  className={cn(
                    "flex-1 rounded-full border px-3 py-2 text-sm transition-colors",
                    mobileTab === tab.value
                      ? "border-secondary bg-secondary text-secondary-foreground"
                      : "border-secondary/30 hover:bg-secondary/10",
                  )}
                >
                  {tab.label}
                  <span className="ml-1 tabular-nums">
                    ({counts?.[tab.value] ?? 0})
                  </span>
                </button>
              ))}
            </div>

            <div className="hidden gap-4 lg:grid lg:grid-cols-3">
              <KanbanColumn
                title="ລໍຖ້າແພັກ"
                count={counts?.paid ?? 0}
                tone="primary"
                orders={columns?.paid ?? []}
                {...columnProps}
              />
              <KanbanColumn
                title="ພ້ອມສົ່ງ"
                count={counts?.ready_to_ship ?? 0}
                tone="secondary"
                orders={columns?.ready_to_ship ?? []}
                {...columnProps}
              />
              <KanbanColumn
                title="ສົ່ງແລ້ວ"
                count={counts?.shipped ?? 0}
                tone="leaf"
                orders={columns?.shipped ?? []}
                {...columnProps}
              />
            </div>

            <div className="lg:hidden">
              {mobileTab === "paid" ? (
                <KanbanColumn
                  title="ລໍຖ້າແພັກ"
                  count={counts?.paid ?? 0}
                  tone="primary"
                  orders={columns?.paid ?? []}
                  {...columnProps}
                />
              ) : null}
              {mobileTab === "ready_to_ship" ? (
                <KanbanColumn
                  title="ພ້ອມສົ່ງ"
                  count={counts?.ready_to_ship ?? 0}
                  tone="secondary"
                  orders={columns?.ready_to_ship ?? []}
                  {...columnProps}
                />
              ) : null}
              {mobileTab === "shipped" ? (
                <KanbanColumn
                  title="ສົ່ງແລ້ວ"
                  count={counts?.shipped ?? 0}
                  tone="leaf"
                  orders={columns?.shipped ?? []}
                  {...columnProps}
                />
              ) : null}
            </div>
          </>
        )}

        {!board.isLoading && flatOrders.length === 0 ? (
          <div className="flex flex-col items-center gap-3 rounded-2xl border border-secondary/20 border-dashed py-16 text-center">
            <Truck className="size-10 text-muted-foreground/50" />
            <p className="text-muted-foreground">
              {scope === "today"
                ? "ມື້ນີ້ບໍ່ມີອໍເດີ້ລໍຖ້າແພັກ/ສົ່ງ"
                : "ບໍ່ມີອໍເດີ້ລໍຖ້າແພັກ/ສົ່ງ"}
            </p>
            <Button
              variant="outline"
              className="rounded-full"
              onClick={() => nav({ to: "/app/orders" })}
            >
              ໄປລາຍການອໍເດີ້
            </Button>
          </div>
        ) : null}
      </Main>

      <BatchBluetoothPrintDialog
        open={batchPrintOpen}
        onOpenChange={setBatchPrintOpen}
        orders={batchPrintOrders}
        title={`ພິມປ້າຍຊຸດ (${batchPrintOrders.length})`}
      />
    </>
  );
}
